import React, { useState, useEffect, useRef } from 'react';
import { useRecaptcha } from '../hooks/useRecaptcha';
import './Chat.css';

// Extract synthesis JSON from response (fallback when backend doesn't parse)
function extractSynthesisJson(str) {
  if (!str || typeof str !== 'string') return null;
  let candidate = str.trim();
  const codeBlockMatch = candidate.match(/```(?:json)?\s*\n?([\s\S]*?)```/);
  if (codeBlockMatch) candidate = codeBlockMatch[1].trim();
  try {
    const parsed = JSON.parse(candidate);
    if (parsed && typeof parsed.synthesis === 'string' && parsed.prompt && typeof parsed.prompt.content === 'string') return parsed;
  } catch (_) {}
  const start = str.indexOf('{');
  if (start < 0) return null;
  let depth = 0;
  for (let i = start; i < str.length; i++) {
    if (str[i] === '{') depth++;
    else if (str[i] === '}') {
      depth--;
      if (depth === 0) {
        try {
          const parsed = JSON.parse(str.slice(start, i + 1));
          if (parsed && typeof parsed.synthesis === 'string' && parsed.prompt && typeof parsed.prompt.content === 'string') return parsed;
        } catch (_) {}
        break;
      }
    }
  }
  return null;
}

// Split synthesis into intro (chat message) and report (Results view)
function splitSynthesis(content) {
  if (!content || typeof content !== 'string') return { intro: '', report: content || '' };
  const markers = [
    /^##\s+Your\s+Personal\s+IP/im,
    /\*\*your\s+personal\s+ip\*\*/i,
    /^\s*\d+\.\s+\*\*[^*]+\*\*/m,
    /^\s*\*\*[^*]+(?:tool|coach|generator|assistant|builder)[^*]*\*\*/im
  ];
  let earliestIdx = content.length;
  for (const re of markers) {
    const match = content.match(re);
    if (match) {
      const idx = content.indexOf(match[0]);
      if (idx >= 0 && idx < earliestIdx) earliestIdx = idx;
    }
  }
  const intro = content.slice(0, earliestIdx).trim();
  const report = content.slice(earliestIdx).trim();
  return { intro, report };
}

// Firefox iOS detection - comprehensive check for all Firefox mobile variants
const isFirefoxMobile = typeof navigator !== 'undefined' && (
  /Firefox|FxiOS/i.test(navigator.userAgent) ||
  (navigator.userAgent.includes('iPhone') && navigator.userAgent.includes('Gecko')) ||
  (navigator.userAgent.includes('iPad') && navigator.userAgent.includes('Gecko'))
);

function Chat({ sessionState, setSessionState, conversationHistory, setConversationHistory, onSynthesisReady }) {
  const { executeRecaptcha } = useRecaptcha();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEmailCapture, setIsEmailCapture] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const synthesisReportRef = useRef(null);
  const requestIdRef = useRef(0);
  const userSubmittedBeforeGreetingReturned = useRef(false);
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory]);

  useEffect(() => {
    if (inputRef.current) autoGrowTextarea(inputRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // Phase C: Body class for collapse chrome when typing
  // Skip on Firefox mobile - focus/classList can throw "Script error"
  useEffect(() => {
    if (isFirefoxMobile) return;
    try {
      if (isTyping) document.body.classList.add('chat-typing');
      else document.body.classList.remove('chat-typing');
    } catch (e) { /* ignore */ }
    return () => {
      try { document.body.classList.remove('chat-typing'); } catch (e) { /* ignore */ }
    };
  }, [isTyping]);

  // Phase B: visualViewport API for keyboard offset
  // Skip on Firefox mobile - can throw when keyboard opens on focus
  useEffect(() => {
    if (isFirefoxMobile) return;
    try {
      if (!window.visualViewport) return;
      const setKeyboardOffset = () => {
        try {
          const vv = window.visualViewport;
          const layoutH = window.innerHeight;
          const vvH = vv.height;
          const keyboard = Math.max(0, layoutH - vvH - vv.offsetTop);
          document.documentElement.style.setProperty('--kbd', (-keyboard) + 'px');
        } catch (e) { /* Firefox iOS */ }
      };
      window.visualViewport.addEventListener('resize', setKeyboardOffset);
      window.visualViewport.addEventListener('scroll', setKeyboardOffset);
      setKeyboardOffset();
      return () => {
        try {
          window.visualViewport.removeEventListener('resize', setKeyboardOffset);
          window.visualViewport.removeEventListener('scroll', setKeyboardOffset);
        } catch (e) { /* Firefox iOS */ }
      };
    } catch (e) { /* Firefox iOS may not support visualViewport */ }
  }, []);

  // When synthesis completes and we transition to email capture, switch to Results view
  useEffect(() => {
    if (!isEmailCapture || !onSynthesisReady) return;
    const content = synthesisReportRef.current;
    if (!content) return;
    const timer = setTimeout(() => {
      try {
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'synthesis_complete');
        }
      } catch (e) { /* Firefox iOS may throw from gtag */ }
      onSynthesisReady(content, conversationHistory);
      synthesisReportRef.current = null;
    }, 1200);
    return () => clearTimeout(timer);
  }, [isEmailCapture, conversationHistory, onSynthesisReady]);

  // Auto-focus input when composer is visible; refocus after each message exchange
  // Skip entirely on Firefox mobile - throws "Script error" on programmatic focus
  useEffect(() => {
    if (isFirefoxMobile || !sessionState || isComplete || isEmailCapture || isLoading) return;
    const id = setTimeout(() => {
      try {
        inputRef.current?.focus();
      } catch (e) { /* ignore */ }
    }, 150);
    return () => clearTimeout(id);
  }, [sessionState, isComplete, isEmailCapture, isLoading, conversationHistory.length]);

  // Request AI's first greeting when session is ready and conversation is empty
  useEffect(() => {
    if (!sessionState || conversationHistory.length > 0) return;

    const currentRequestId = ++requestIdRef.current;

    const requestInitialGreeting = async () => {
      const initialMessage = "I'm ready to begin";  // Sent to API only, not displayed
      setIsLoading(true);
      setError(null);

      try {
        const token = await executeRecaptcha('chat_message');
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: initialMessage,
            conversationHistory: [],
            sessionState: sessionState,
            recaptcha_token: token
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to start interview');
        }

        const data = await response.json();

        // Only apply if this is still the current request
        if (currentRequestId !== requestIdRef.current) return;
        if (userSubmittedBeforeGreetingReturned.current) return;

        const assistantMessage = { role: 'assistant', content: data.response, phase: data.sessionState?.currentPhase };
        // Don't show the synthetic "I'm ready to begin" — start with the AI's greeting only
        setConversationHistory([assistantMessage]);
        setSessionState(data.sessionState);

        if (data.phaseInfo.isEmailCapture) setIsEmailCapture(true);
        if (data.phaseInfo.isExit) setIsComplete(true);
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error('Initial greeting error:', err);
        setError(err.message || 'Apologies—we couldn\'t start the interview. Please try again in a few minutes, or email us at hello@xf.works.');
        setConversationHistory([]);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    requestInitialGreeting();

    // Cleanup: invalidate this request (use captured variable per react-hooks/exhaustive-deps)
    return () => {
      requestIdRef.current = currentRequestId + 1;
    };
  }, [sessionState, conversationHistory.length, setConversationHistory, setSessionState, executeRecaptcha]);

  const autoGrowTextarea = (el) => {
    if (!el) return;
    el.style.height = '0px';
    const next = Math.min(el.scrollHeight, 160);
    el.style.height = next + 'px';
    requestAnimationFrame(() => scrollToBottom());
  };

  const scrollToBottom = () => {
    try {
      const isTouch = 'ontouchstart' in window || window.matchMedia('(pointer: coarse)').matches;

      requestAnimationFrame(() => {
        try {
          messagesEndRef.current?.scrollIntoView({
            behavior: isTouch ? 'auto' : 'smooth',
            block: 'nearest'
          });
        } catch (e) { /* ignore */ }
      });
    } catch (e) { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;

    userSubmittedBeforeGreetingReturned.current = true;
    setError(null);

    // Add user message to conversation (include phase for backend synthesis extraction)
    const userMessage = { role: 'user', content: trimmedInput, phase: sessionState?.currentPhase };
    const updatedHistory = [...conversationHistory, userMessage];
    setConversationHistory(updatedHistory);
    setInput('');
    setIsLoading(true);

    const isRandomCommand = /^\[random(?:\s*[-=#]\s*\w+)?\]$/i.test(trimmedInput);

    try {
      const token = await executeRecaptcha('chat_message');
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmedInput,
          isRandomCommand: isRandomCommand,
          conversationHistory: conversationHistory,
          sessionState: sessionState,
          recaptcha_token: token
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const data = await response.json();

      // When synthesis completes: handle new JSON format or legacy text format
      // Also support frontend fallback: if backend didn't transition but response contains valid synthesis JSON, treat as complete
      const wasSynthesisPhase = sessionState?.currentPhase === 'synthesis';
      const backendTransitioned = data.phaseInfo.isEmailCapture;
      const fallbackParsed = !backendTransitioned && wasSynthesisPhase ? extractSynthesisJson(data.response) : null;
      const isSynthesisComplete = backendTransitioned || fallbackParsed;

      if (isSynthesisComplete) {
        let reportContent = data.response;
        let chatContent = 'Here\'s your Personal IP report.';
        let parsed = backendTransitioned ? null : fallbackParsed;
        if (!parsed) {
          try {
            parsed = JSON.parse(data.response);
          } catch (_) {}
        }
        if (parsed && typeof parsed.synthesis === 'string' && parsed.prompt && typeof parsed.prompt.content === 'string') {
          reportContent = parsed;
          chatContent = parsed.synthesis ? parsed.synthesis.slice(0, 120) + (parsed.synthesis.length > 120 ? '...' : '') : chatContent;
        } else {
          const { intro, report } = splitSynthesis(data.response);
          chatContent = intro || data.response;
          reportContent = report || data.response;
        }
        const assistantMessage = { role: 'assistant', content: chatContent, phase: 'synthesis' };
        setConversationHistory([...updatedHistory, assistantMessage]);
        synthesisReportRef.current = reportContent;
      } else {
        const assistantMessage = { role: 'assistant', content: data.response, phase: data.sessionState?.currentPhase };
        setConversationHistory([...updatedHistory, assistantMessage]);
      }

      // Update session state (use fallback phase if we parsed synthesis on frontend)
      setSessionState(fallbackParsed ? { ...data.sessionState, currentPhase: 'email' } : data.sessionState);

      // Check if we're in email capture or exit phase
      if (isSynthesisComplete) {
        setIsEmailCapture(true);
      }

      if (data.phaseInfo.isExit) {
        setIsComplete(true);
      }

    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message || 'So sorry—something went wrong and we\'re not sure what. Please try again later, or email us at hello@xf.works.');
    } finally {
      setIsLoading(false);
    }
  };

  // Phase order for progress indicator (welcome through synthesis; email/exit are end states)
  const PHASE_ORDER = ['welcome', 'background', 'ai_experience', 'expertise', 'values', 'prompt_type', 'synthesis'];
  const getPhaseLabel = (phase) => {
    const idx = PHASE_ORDER.indexOf(phase);
    if (idx < 0) return null;
    return `${PHASE_ORDER[idx].replace(/_/g, ' ').toUpperCase()} (${idx + 1}/${PHASE_ORDER.length})`;
  };

  const formatMessage = (content) => {
    if (!content || typeof content !== 'string') return null;
    // Normalize literal \n to actual newlines (fixes JSON-escaped content from synthesis)
    const normalized = content.replace(/\\n/g, '\n');
    const lines = normalized.split('\n');
    return lines.map((line, idx) => {
      // Check if line looks like code (starts with spaces or contains special markers)
      if (line.match(/^ {4}/) || line.match(/^```/)) {
        return <div key={idx} className="code-block">{line}</div>;
      }
      return <div key={idx}>{line || <br />}</div>;
    });
  };

  return (
    <div className="chat-container">
      <div className="terminal-frame chat-frame">
        <div className="terminal-titlebar">
          XF - Personal IP Discovery Tool
          <span className="text-muted" style={{ fontSize: '10px', opacity: 0.7 }}>v2</span>
        </div>

        <div className="chat-messages terminal-content" role="log" aria-live="polite" aria-atomic="false">
          <div className="chat-messages-spacer" aria-hidden="true" />
          {conversationHistory.length === 0 && !error && (
            <div className="message assistant-message">
              <span className="message-prefix text-primary">system:</span>
              <div className="message-content text-content">
                <span className="typing-indicator">Initializing interview</span>
              </div>
            </div>
          )}

          {conversationHistory.map((msg, idx) => (
            <div
              key={idx}
              className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
            >
              <span className={`message-prefix ${msg.role === 'user' ? 'text-accent' : 'text-primary'}`}>
                {msg.role === 'user' ? '> you:' : 'system:'}
              </span>
              <div className="message-content text-content">
                {msg.role === 'assistant' && getPhaseLabel(msg.phase || sessionState?.currentPhase) && (
                  <div className="message-phase text-muted">{`// ${getPhaseLabel(msg.phase || sessionState?.currentPhase)}`}</div>
                )}
                {formatMessage(msg.content)}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message assistant-message">
              <span className="message-prefix text-primary">system:</span>
              <div className="message-content text-content">
                {['values', 'synthesis'].includes(sessionState?.currentPhase) ? (
                  <span className="typing-indicator">Generating report… hold on</span>
                ) : (
                  <>
                    {getPhaseLabel(sessionState?.currentPhase) && (
                      <div className="message-phase text-muted">{`// ${getPhaseLabel(sessionState?.currentPhase)}`}</div>
                    )}
                    <span className="typing-indicator"></span>
                  </>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="error-box">
              <span className="message-prefix text-error">error:</span>
              <div className="message-content text-error">{error}</div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {!isComplete && isEmailCapture && (
          <div className="chat-input-container">
            <p className="text-muted" style={{ fontSize: '14px' }}>Loading your report...</p>
          </div>
        )}

        {isComplete && (
          <div className="chat-complete terminal-content">
            <p className="text-primary text-center">
              Session complete. Thank you.
            </p>
          </div>
        )}

        {!isComplete && !isEmailCapture && (
          <div className="chat-composer">
          <div className="chat-composer-inner chat-input-container">
            <form onSubmit={handleSubmit} className="chat-form">
              <div className="chat-form-row">
                {/* Accessibility: Hidden label for screen readers */}
                <label htmlFor="chat-input" className="visually-hidden">
                  Type your response to the interview question
                </label>

                {isFirefoxMobile ? (
                  <input
                    id="chat-input"
                    ref={inputRef}
                    type="text"
                    className="terminal-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    placeholder="type your response..."
                    disabled={isLoading && conversationHistory.length > 0}
                    maxLength={500}
                    aria-required="true"
                    aria-label="Your response to the interview question"
                  />
                ) : (
                  <textarea
                    id="chat-input"
                    ref={inputRef}
                    className="terminal-input"
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      autoGrowTextarea(e.target);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    onFocus={() => {
                      try {
                        requestAnimationFrame(() => setIsTyping(true));
                      } catch (e) { /* ignore */ }
                    }}
                    onBlur={() => {
                      try {
                        requestAnimationFrame(() => setIsTyping(false));
                      } catch (e) { /* ignore */ }
                    }}
                    placeholder="type your response..."
                    disabled={isLoading && conversationHistory.length > 0}
                    maxLength={500}
                    rows={1}
                    aria-required="true"
                    aria-label="Your response to the interview question"
                  />
                )}
                <button
                  type="submit"
                  className="chat-send-btn"
                  disabled={!input.trim() || isLoading}
                >
                  [send]
                </button>
              </div>
              {input.length > 400 && (
                <div className="input-meta">
                  <span className="text-muted">
                    {500 - input.length} chars remaining
                  </span>
                </div>
              )}
            </form>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export default Chat;
